import { useMemo, useState } from 'react';

import { Instance, Instances } from '@spazious/dollhouse-three';
import { useEventCallback } from '@spazious/react-hooks';

import { ModelContext, ModelContextValue } from './ModelContext.js';
import { ModelInstances, ModelInstancesProps } from './ModelInstances.js';

export interface ModelProviderProps extends Pick<ModelInstancesProps, 'inject'> {
  children: React.ReactNode;
}

export const ModelProvider: React.FC<ModelProviderProps> = ({ children, ...restProps }) => {
  const [parentRefs, setParentRefs] = useState<Record<string, Instances>>({});
  const [instances, setInstances] = useState<Record<string, Instance[]>>({});

  const getParent = useEventCallback((url: string) => {
    return parentRefs[url];
  });

  const subscribe = useEventCallback((url: string, ref: React.RefObject<Instance>) => {
    const instance = ref.current;
    if (instance) {
      setInstances((instances) => {
        const newInstances = [...(instances[url] ?? []), instance];
        return { ...instances, [url]: newInstances };
      });
    }

    return () => {
      if (instance) {
        setInstances((instances) => {
          const newInstances = instances[url].filter((item) => item !== instance);
          return { ...instances, [url]: newInstances };
        });
      }
    };
  });

  const value = useMemo<ModelContextValue>(() => {
    return {
      getParent,
      subscribe,
    };
  }, [getParent, subscribe]);

  const rootNode = useMemo(
    () =>
      Object.entries(instances).reduce(
        (children, [url, instances]) => (
          <ModelInstances instances={instances} setRef={setParentRefs} url={url} {...restProps}>
            {children}
          </ModelInstances>
        ),
        <ModelContext.Provider value={value}>{children}</ModelContext.Provider>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [children, instances, value]
  );

  return rootNode;
};
